import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Secciones/Header';
import Footer from '../components/Secciones/Footer';
import './PrivacyPolicy.css';

const sections = [
  { id: 'information', label: 'Information we collect' },
  { id: 'use', label: 'How we use information' },
  { id: 'recommendations', label: 'Recommendations and browser storage' },
  { id: 'sharing', label: 'How we share information' },
  { id: 'retention', label: 'Retention and security' },
  { id: 'choices', label: 'Your choices and rights' },
  { id: 'contact', label: 'Contact us' }
];

const PrivacyPolicy = () => (
  <div className="pp-shell">
    <Header />
    <main className="pp-page" aria-labelledby="privacy-title">
      <header className="pp-hero">
        <div className="pp-container">
          <span className="pp-eyebrow">Legal & privacy</span>
          <h1 id="privacy-title">Privacy Policy</h1>
          <p>
            This policy explains how AMG Forwarding Corporation, doing business as
            Valley International Supply (“Valley,” “we,” “us,” or “our”), collects,
            uses, stores, and shares information through this website and our related
            customer services.
          </p>
          <div className="pp-effective"><span /> Effective August 17, 2026</div>
        </div>
      </header>

      <div className="pp-container pp-layout">
        <aside className="pp-navigation" aria-label="Privacy policy contents">
          <strong>On this page</strong>
          <nav>
            {sections.map((section, index) => (
              <a href={`#${section.id}`} key={section.id}>
                <span>{String(index + 1).padStart(2, '0')}</span>{section.label}
              </a>
            ))}
          </nav>
        </aside>

        <article className="pp-content">
          <section className="pp-introduction">
            <p>
              This policy applies when you browse our catalog, create or use an account,
              request a quote, contact us, place an order, or otherwise interact with
              Valley online. It does not cover third-party websites or services that
              maintain their own privacy policies.
            </p>
          </section>

          <section id="information">
            <span className="pp-section-number">01</span>
            <h2>Information we collect</h2>
            <p>Depending on how you use the website, we may collect:</p>
            <ul>
              <li><strong>Account and profile information,</strong> such as your name, work email, password in hashed form, company, telephone number, country, postal code, and billing or shipping addresses.</li>
              <li><strong>Order and transaction information,</strong> including products, quantities, prices, delivery information, order status, quotes, returns, and related communications.</li>
              <li><strong>Contact and support information,</strong> including information you submit in contact, quote, product inquiry, or customer-service forms.</li>
              <li><strong>Payment information.</strong> Card details are collected and processed by Stripe. Valley receives information needed to confirm and administer the payment, but our website does not store full payment-card numbers.</li>
              <li><strong>Technical and usage information,</strong> such as IP address, request date and time, browser or device information, pages requested, security events, and products viewed.</li>
              <li><strong>Information stored in your browser,</strong> including session credentials, guest-cart contents, and limited product-view history used for recommendations.</li>
            </ul>
          </section>

          <section id="use">
            <span className="pp-section-number">02</span>
            <h2>How we use information</h2>
            <p>We use information to:</p>
            <ul>
              <li>provide the product catalog, accounts, carts, quotes, orders, payments, shipping, and customer support you request;</li>
              <li>verify accounts, authenticate users, send service communications, and maintain order records;</li>
              <li>personalize in-stock product recommendations and improve navigation;</li>
              <li>protect the website, prevent spam and fraud, enforce rate limits, troubleshoot errors, and maintain availability;</li>
              <li>comply with legal, tax, accounting, export, and recordkeeping obligations; and</li>
              <li>establish, exercise, or defend legal claims and enforce our <Link to="/terms">Terms and Conditions</Link>.</li>
            </ul>
          </section>

          <section id="recommendations">
            <span className="pp-section-number">03</span>
            <h2>Recommendations and browser storage</h2>
            <p>
              When you open a product page, the website stores the product ID, brand,
              and category for up to 20 recently viewed products in your browser’s local
              storage. We use those signals to rank similar in-stock products in the
              “Recommended for you” section. If there is no view history or no matching
              product, the section displays a random selection of in-stock products.
            </p>
            <p>
              This recommendation process does not make decisions that produce legal or
              similarly significant effects. You can reset it at any time by clearing
              this website’s stored site data in your browser. Local storage is also used
              to keep you signed in and to retain a guest cart. Blocking or clearing it
              may sign you out or remove locally saved cart and recommendation data.
            </p>
            <div className="pp-note">
              <i className="fas fa-shield-alt" aria-hidden="true" />
              <p><strong>No advertising profile.</strong> We do not use the product-view history described above to sell personal information or serve cross-site targeted advertising.</p>
            </div>
          </section>

          <section id="sharing">
            <span className="pp-section-number">04</span>
            <h2>How we share information</h2>
            <p>We may disclose information to the following recipients when reasonably necessary:</p>
            <ul>
              <li><strong>Service providers</strong> that host our website or databases, deliver email, provide security, process payments, or support business operations. Payment processing is provided by Stripe and is also subject to <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe’s Privacy Policy</a>.</li>
              <li><strong>Shipping, manufacturer, and fulfillment partners</strong> involved in quoting, supplying, or delivering requested products.</li>
              <li><strong>Professional advisers and authorities</strong> when required to comply with law, protect rights and safety, investigate misuse, or respond to lawful process.</li>
              <li><strong>Parties to a business transaction</strong> involving a merger, financing, reorganization, sale, or transfer of some or all of our business, subject to appropriate confidentiality measures.</li>
            </ul>
            <p>We do not sell personal information collected through this website.</p>
          </section>

          <section id="retention">
            <span className="pp-section-number">05</span>
            <h2>Retention and security</h2>
            <p>
              We retain personal information for as long as reasonably necessary for the
              purposes described in this policy, including providing services, maintaining
              transaction and warranty records, meeting legal or accounting requirements,
              resolving disputes, and protecting the website. Retention periods vary based
              on the type of information and the reason it was collected.
            </p>
            <p>
              We use administrative, technical, and organizational safeguards designed to
              protect information. No internet transmission or storage system is completely
              secure, so we cannot guarantee absolute security. You are responsible for
              protecting your account credentials and notifying us if you suspect misuse.
            </p>
          </section>

          <section id="choices">
            <span className="pp-section-number">06</span>
            <h2>Your choices and rights</h2>
            <p>
              Depending on where you live, applicable law may allow you to request access
              to, correction of, deletion of, or a copy of certain personal information,
              or to object to or restrict certain processing. We may need to verify your
              identity before completing a request and may retain information where legally
              permitted or required.
            </p>
            <p>
              To make a privacy request, email
              <a href="mailto:inquiry@valleyintlsupply.com"> inquiry@valleyintlsupply.com</a>.
              You may also update certain account or address information through the website
              or by contacting us. Clearing your browser’s site data removes locally stored
              guest-cart, session, and product-view data from that browser.
            </p>
          </section>

          <section>
            <span className="pp-section-number">07</span>
            <h2>International users and children</h2>
            <p>
              Valley supports customers across the Americas. Information may be processed
              in the United States and other countries where Valley or its service providers
              operate. Those countries may have different data-protection laws from your
              country of residence.
            </p>
            <p>
              This business-to-business website is not directed to children under 13, and
              we do not knowingly collect personal information from children under 13. If
              you believe a child has submitted personal information, please contact us.
            </p>
          </section>

          <section>
            <span className="pp-section-number">08</span>
            <h2>Changes to this policy</h2>
            <p>
              We may update this policy to reflect changes in our services, practices, or
              legal obligations. We will post the revised policy here and update the effective
              date. Material changes may also be communicated through the website or another
              appropriate channel.
            </p>
          </section>

          <section id="contact" className="pp-contact">
            <span className="pp-section-number">09</span>
            <h2>Contact us</h2>
            <p>Questions or requests concerning this policy can be sent to:</p>
            <address>
              <strong>AMG Forwarding Corporation d/b/a Valley International Supply</strong><br />
              1474 W. Price Rd., Ste. 107<br />
              Brownsville, Texas 78520, USA<br />
              <a href="mailto:inquiry@valleyintlsupply.com">inquiry@valleyintlsupply.com</a><br />
              <a href="tel:+19563380631">+1 956-338-0631</a>
            </address>
          </section>
        </article>
      </div>
    </main>
    <Footer />
  </div>
);

export default PrivacyPolicy;

